/**
 * New requests: the client's copy of the one list (server:
 * src/server/services/requests/requestsList.ts, GET /api/requests).
 * The chip, the page, its lanes and the Grid signpost all read this one
 * query, so they can never disagree.
 */
import type { TriesSummary } from "@/lib/new-requests";

export type RequestStage = "new" | "trying" | "booked" | "closed";
export type RequestTone = "blue" | "amber" | "red" | "good" | "quiet";
export type ClosedReason = "quote_needed" | "not_interested" | "cant_reach" | "spam" | "duplicate" | "answered";

export interface RequestItem {
    id: string;
    stage: RequestStage;
    name: string;
    contactName: string | null;
    phone: string | null;
    email: string | null;
    channel: "call" | "text" | "email";
    textOk: boolean;
    site: string | null;
    city: string | null;
    what: string;
    sourceKind: "website" | "reminder" | "phone" | "email";
    cameFrom: string;
    createdAt: string;
    age: string;
    tone: RequestTone;
    when: string;
    bestTimes: string | null;
    tries: TriesSummary;
    jobId: string | null;
    jobNumber: string | null;
    scheduledStart: string | null;
    leadId: string | null;
    owner: { userId: string; firstName: string } | null;
    scheduleLock: string | null;
    closedReason: ClosedReason | null;
    closedLine: string | null;
}

export interface RequestsView {
    now: string;
    counts: Record<RequestStage, number>;
    pill: { count: number; late: number; tone: "none" | "blue" | "amber" | "red"; label: string };
    items: RequestItem[];
}

export const REQUESTS_QUERY_KEY = ["requests"] as const;

export const LANES: ReadonlyArray<{ stage: RequestStage; label: string; color: string }> = [
    { stage: "new", label: "New", color: "#004AAD" },
    { stage: "trying", label: "Trying to reach", color: "#C9772B" },
    { stage: "booked", label: "Booked this week", color: "#2f7d4f" },
    { stage: "closed", label: "Closed", color: "#8792a5" },
];

export const NOT_INTERESTED_WHY = [
    "Using another company",
    "Already tested this year",
    "Price",
    "Moved or sold",
    "Other",
] as const;

export function telHref(phone: string | null): string | null {
    const digits = (phone ?? "").replace(/[^\d+]/g, "");
    return digits ? `tel:${digits}` : null;
}
